import React from "react";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import {
  translateService,
  stateService,
  workSpacesService,
  htmlHelperService,
  signService,
  sessionService,
} from "../../services/imports";
import shareService from "../../services/shareService";
import oidcService from "../../services/oidcService";
import { LoadingButton } from "../formcontrols/imports";
import PlacementView from "../faceSign/placementView";
import { describeSignerMatch } from "./signerMatch";
import { states } from "../../models/states";

// the backend's own limits (ShareService.validate), so the dialog refuses first
const MAX_RECIPIENTS = 50;
const MAX_GRANTS = 10;
const MAX_MESSAGE = 1000;
const MAX_DEADLINE_MS = 5 * 366 * 24 * 60 * 60 * 1000;
const KIND_PATTERN = /^[A-Z0-9_]{1,32}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// the visible signature's size when the signing options give none, in pt
const DEFAULT_BOX = { width: 180, height: 60 };
// the backend's signatory profiles: Georgia only today
const COUNTRIES = ["GEO"];

let nextKey = 1;
const emptyRecipient = () => ({
  key: nextKey++,
  email: "",
  permission: "READ",
  signerIds: [],
  signerKinds: [],
  signatory: false,
  country: COUNTRIES[0],
  personalNumber: "",
  placing: false,
  placement: null,
  prefilledFrom: null,
});

// sharing again: the recipient as their active share holds it
const recipientFromShare = (share) => {
  const rs = share.requiredSignatory;
  return {
    ...emptyRecipient(),
    email: share.email,
    permission: share.permission,
    signerIds: share.signerIds || [],
    signerKinds: share.signerKinds || [],
    signatory: !!rs,
    country: (rs && rs.country) || COUNTRIES[0],
    personalNumber: (rs && rs.attributes && rs.attributes.personalNumber) || "",
    placing: !!share.placement,
    placement: share.placement || null,
    prefilledFrom: share.shareId,
  };
};

const digitsOf = (value) => String(value || "").replace(/\s+/g, "");

// epoch ms <-> the datetime-local field (local time, to the minute)
const toLocalInput = (ms) => {
  if (!ms) return "";
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// The share dialog (share-prompt tasks 8, 17 and 18): recipients with READ or
// SIGN, SIGN granting signers (by uuid) and kinds from the signed listing,
// an optional required signatory, an optional fixed place for the signature,
// the deadline, the message, and inside an organization's workspace the
// "inside {organization}" toggle. The document's shares are listed on its
// page (DocumentShares); "share again" there opens this dialog with prefill.
//
// A share someone has signed through (its signedAt set) keeps its required
// signatory, or its absence, for good (vsys signings-runbook §4): shared
// again, the dialog shows it read-only, offers no READ when it holds one,
// and offers to withdraw it and share again - a new share - to ask another
// person.
//
// A share an integration made with a signer match (signerMatch: criteria a
// signing tool must meet, instead of signing tools and kinds) keeps it when
// shared again: shown read-only, sent back exactly as listed, and it needs
// no signed listing (delta 7). The dialog itself never makes criteria.
//
// props: isOpen, docId (the exposed id), doc (the v2 read, optional: read here otherwise),
// prefill (a share to share again, optional), onClose(), onShared() (the
// shares changed: shared, or a signed share withdrawn)
export default class ShareDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.initialState();
  }

  initialState() {
    return {
      doc: null,
      readError: null,
      listing: null, // { body, signature, listing }
      listingLoading: false,
      listingError: false,
      recipients: [emptyRecipient()],
      deadline: "",
      message: "",
      insideOrganization: true,
      errors: {}, // recipient key -> [text]
      formErrors: [],
      sending: false,
      // the wizard: 0 = the form; n = the signature place of the nth SIGN
      // recipient whose place is fixed
      step: 0,
      refusal: null,
      shared: null, // the last answer's shares
      confirmDrop: null, // addresses whose required signatory a resend drops
      existing: {}, // address (lower case) -> its active share from the caller, or null
      confirmWithdraw: null, // a signed share's id, "withdraw, then share again" asked
      withdrawing: null,
      withdrawn: {}, // address (lower case) -> its signed share was withdrawn here
      docFile: null,
      box: null,
    };
  }

  componentDidMount() {
    if (this.props.isOpen) this.open();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.isOpen && (!prevProps.isOpen || prevProps.docId !== this.props.docId)) this.open();
    else if (
      prevState.recipients !== this.state.recipients ||
      prevState.doc !== this.state.doc ||
      prevState.existing !== this.state.existing
    )
      this.ensureListing();
  }

  componentWillUnmount() {
    this.closed = true;
  }

  t = (k, ...a) => translateService.t(`shareDialog.${k}`, ...a);

  // ---- loading -----------------------------------------------------------

  open() {
    // the dialog stays mounted between documents: the placement context too
    this.placementRequested = false;
    this.lookups = {};
    this.setState(this.initialState(), () => {
      if (this.props.prefill) this.shareAgain(this.props.prefill);
      if (this.props.doc) this.takeDoc(this.props.doc);
      else
        workSpacesService.getDocumentResult(this.props.docId, { skipCustomErrorHandling: true }).subscribe(
          (res) => this.takeDoc(res.data),
          (e) => this.setState({ readError: signService.problemMessage(e, translateService.t("error.error")) })
        );
    });
  }

  // what needs the document runs once it is in the state (inside a setState
  // callback the update is batched: this.state.doc is still null right after)
  takeDoc(doc) {
    if (this.closed) return;
    this.setState({ doc }, () => {
      // a prefilled share with a fixed place needs the document for it
      if (this.state.recipients.some((r) => r.placing)) this.loadPlacementContext();
      // the signed listing: once a recipient needs it (ensureListing)
    });
  }

  // the sharer's signers of the workspace, signed by the gateway; fetched
  // once a recipient needs it and again when the backend says it expired
  loadListing = () =>
    new Promise((resolve) => {
      const doc = this.state.doc;
      if (!doc) return resolve(null);
      this.setState({ listingLoading: true, listingError: false });
      signService.getSignerListing(doc.workspaceExposedId).subscribe(
        (listing) => {
          if (!this.closed) this.setState({ listing, listingLoading: false });
          resolve(listing);
        },
        () => {
          if (!this.closed) this.setState({ listingLoading: false, listingError: true });
          resolve(null);
        }
      );
    });

  // the document's bytes and the signature's size, for a fixed place
  loadPlacementContext() {
    const doc = this.state.doc;
    if (!doc || this.placementRequested) return;
    this.placementRequested = true;
    htmlHelperService
      .getPdfBytes(doc.docPath)
      .then((docFile) => !this.closed && this.setState({ docFile }))
      .catch(() => {
        this.placementRequested = false;
        this.setState({ formErrors: [this.t("documentUnavailable")] });
      });
    workSpacesService.getSigningOptions(doc.docExposedId, { skipCustomErrorHandling: true }).subscribe(
      (res) => {
        const d = res.data && res.data.visualSignature && res.data.visualSignature.dimensions;
        this.setState({ box: d && d.width > 0 && d.height > 0 ? { width: d.width, height: d.height } : DEFAULT_BOX });
      },
      () => this.setState({ box: DEFAULT_BOX })
    );
  }

  // ---- the listing ---------------------------------------------------------

  parsed = () => (this.state.listing && this.state.listing.listing) || null;

  grantableSigners = () => ((this.parsed() && this.parsed().signers) || []).filter((s) => s.grantable && s.uuid);

  grantableKinds = () => (this.parsed() && this.parsed().grantableKinds) || [];

  signerEntry = (uuid) => ((this.parsed() && this.parsed().signers) || []).find((s) => s.uuid === uuid);

  kindEntry = (kind) => this.grantableKinds().find((k) => k.kind === kind);

  // every chosen signer and kind can check a required signatory
  supportsLimits = (r) =>
    r.signerIds.every((id) => (this.signerEntry(id) || {}).supportsLimits === true) &&
    r.signerKinds.every((k) => (this.kindEntry(k) || {}).supportsLimits === true);

  // ---- editing -------------------------------------------------------------

  updateRecipient = (index, change) =>
    this.setState((s) => {
      const recipients = s.recipients.slice();
      recipients[index] = { ...recipients[index], ...change };
      const errors = { ...s.errors };
      delete errors[recipients[index].key];
      return { recipients, errors, refusal: null, shared: null, confirmDrop: null };
    });

  addRecipient = () =>
    this.setState((s) =>
      s.recipients.length >= MAX_RECIPIENTS ? null : { recipients: [...s.recipients, emptyRecipient()] }
    );

  removeRecipient = (index) =>
    this.setState((s) => ({ recipients: s.recipients.filter((_, i) => i !== index) }));

  toggleGrant = (index, field, value) => {
    const r = this.state.recipients[index];
    const list = r[field].includes(value) ? r[field].filter((v) => v !== value) : [...r[field], value];
    this.updateRecipient(index, { [field]: list });
  };

  // the active share an address already holds from the caller, as looked up
  // (undefined until then)
  activeShareFor = (email) => {
    const address = String(email || "").trim().toLowerCase();
    return address ? this.state.existing[address] : undefined;
  };

  // The list's search (q) for the address, the active shares only: the one
  // whose address is exactly it. One request per address; a failed lookup
  // counts as none (the backend still applies its own rules).
  lookupActiveShare = (email) => {
    const address = String(email || "").trim().toLowerCase();
    if (!address || !this.state.doc) return Promise.resolve(null);
    if (!this.lookups[address])
      this.lookups[address] = shareService
        .list(this.state.doc.docExposedId, { all: false, q: address, pageSize: 20 })
        .toPromise()
        .then(
          (res) => ((res.data && res.data.shares) || []).find((s) => String(s.email).toLowerCase() === address) || null,
          () => null
        )
        .then((share) => {
          if (!this.closed) this.setState((s) => ({ existing: { ...s.existing, [address]: share } }));
          return share;
        });
    return this.lookups[address];
  };

  // an address that already holds an active share from the caller: its
  // settings, the required signatory included, are filled in (task 18)
  onEmailBlur = (index) => {
    const key = this.state.recipients[index] && this.state.recipients[index].key;
    this.lookupActiveShare(this.state.recipients[index].email).then((share) => {
      const at = this.state.recipients.findIndex((x) => x.key === key);
      const r = this.state.recipients[at];
      if (!share || !r || String(r.email).trim().toLowerCase() !== String(share.email).toLowerCase()) return;
      const untouched =
        r.permission === "READ" && !r.signerIds.length && !r.signerKinds.length && !r.signatory && !r.placing;
      if (r.prefilledFrom !== share.shareId && untouched)
        this.updateRecipient(at, { ...recipientFromShare(share), key: r.key });
      else this.syncLocked(share);
    });
  };

  // the active share an address holds that someone has signed through: its
  // required signatory can no longer change; null otherwise
  lockedShareFor = (email) => {
    const share = this.activeShareFor(email);
    return share && share.signedAt ? share : null;
  };

  // the signer match a SIGN recipient's active share gives, kept as it is;
  // null for a share of signing tools and kinds, or none
  matchOf = (r) => {
    if (r.permission !== "SIGN") return null;
    const share = this.activeShareFor(r.email);
    return (share && share.signerMatch) || null;
  };

  // the signed listing, read once a SIGN recipient picks signing tools or
  // kinds - never for a signer match alone
  ensureListing = () => {
    const { doc, listing, listingLoading, listingError, recipients } = this.state;
    if (!doc || !(doc.actionRights && doc.actionRights.sharing) || listing || listingLoading || listingError) return;
    if (recipients.some((r) => r.permission === "SIGN" && !this.matchOf(r))) this.loadListing();
  };

  // a signed share's rows take its required signatory, or its absence, as
  // it stands (the refusal on the screen stays)
  syncLocked = (share) => {
    if (!share || !share.signedAt || this.closed) return;
    const address = String(share.email).toLowerCase();
    const rs = share.requiredSignatory;
    this.setState((s) => ({
      recipients: s.recipients.map((r) =>
        String(r.email).trim().toLowerCase() !== address
          ? r
          : {
              ...r,
              signatory: !!rs,
              country: (rs && rs.country) || r.country,
              personalNumber: (rs && rs.attributes && rs.attributes.personalNumber) || "",
            }
      ),
    }));
  };

  // after a refusal (400): the shares may have changed meanwhile - someone
  // signed through one, which locks its required signatory - so every
  // address is looked up again, past the cache, and its rows locked
  refreshLookups = () => {
    this.lookups = {};
    this.state.recipients.forEach((r) => this.lookupActiveShare(r.email).then(this.syncLocked));
  };

  // "withdraw, then share again": the signed share is withdrawn, so sharing
  // now makes a new share, with a required signatory of its own
  withdrawLocked = (share) => {
    const doc = this.state.doc;
    if (!doc || this.state.withdrawing) return;
    const address = String(share.email).toLowerCase();
    this.setState({ withdrawing: share.shareId, confirmWithdraw: null });
    shareService.withdraw(doc.docExposedId, share.shareId).subscribe(
      () => {
        if (this.closed) return;
        delete this.lookups[address];
        this.setState((s) => ({
          withdrawing: null,
          refusal: null,
          existing: { ...s.existing, [address]: null },
          withdrawn: { ...s.withdrawn, [address]: true },
          recipients: s.recipients.map((r) =>
            String(r.email).trim().toLowerCase() === address ? { ...r, prefilledFrom: null } : r
          ),
        }));
        if (this.props.onShared) this.props.onShared();
      },
      (e) =>
        !this.closed &&
        this.setState({ withdrawing: null, refusal: signService.problemMessage(e, this.t("shareError")) })
    );
  };

  // "Share again" from the document's share list: that recipient, as their
  // share holds it
  shareAgain = (share) => {
    this.setState((s) => {
      const recipient = recipientFromShare(share);
      const only = s.recipients.length === 1 && !s.recipients[0].email.trim();
      return {
        recipients: only ? [recipient] : [...s.recipients, recipient],
        deadline: s.deadline || toLocalInput(share.deadline),
        message: s.message || share.message || "",
        existing: { ...s.existing, [String(share.email).toLowerCase()]: share },
        shared: null,
        refusal: null,
      };
    });
    if (share.placement) this.loadPlacementContext();
    if (this.formRef) this.formRef.scrollIntoView({ block: "start" });
  };

  // ---- checking ------------------------------------------------------------

  // the backend's rules, in the dialog first; withPlacement false: the form
  // step's check, the signature places come in the next steps
  check(withPlacement = true) {
    const t = this.t;
    const { recipients, deadline, message } = this.state;
    const errors = {};
    const formErrors = [];
    const own = this.ownEmail();
    const seen = {};
    const add = (key, text) => (errors[key] = [...(errors[key] || []), text]);

    if (!recipients.length) formErrors.push(t("errorNoRecipients"));
    if (recipients.length > MAX_RECIPIENTS) formErrors.push(t("errorTooManyRecipients", MAX_RECIPIENTS));

    for (const r of recipients) {
      const email = r.email.trim().toLowerCase();
      if (!EMAIL_PATTERN.test(email)) add(r.key, t("errorEmail"));
      else if (seen[email]) add(r.key, t("errorDuplicate"));
      else if (own && email === own) add(r.key, t("errorSelf"));
      seen[email] = true;

      // a signed share holding a required signatory is shared for signing
      // only: READ would remove it
      const locked = this.lockedShareFor(r.email);
      if (locked && locked.requiredSignatory && r.permission !== "SIGN") add(r.key, t("errorLockedRead"));

      if (r.permission !== "SIGN") continue;
      // a signer match grants no signing tool or kind: the backend checks it
      const match = this.matchOf(r);
      const grants = r.signerIds.length + r.signerKinds.length;
      if (!match) {
        if (!grants) add(r.key, t("errorNoGrant"));
        if (grants > MAX_GRANTS) add(r.key, t("errorTooManyGrants", MAX_GRANTS));
        if (r.signerKinds.some((k) => !KIND_PATTERN.test(k))) add(r.key, t("errorKind"));
      }
      if (r.signatory) {
        if (!COUNTRIES.includes(r.country)) add(r.key, t("errorCountry"));
        if (!/^\d{11}$/.test(digitsOf(r.personalNumber))) add(r.key, t("errorPersonalNumber"));
        if (!match && !this.supportsLimits(r)) add(r.key, t("errorNoLimits"));
      }
      if (withPlacement && r.placing && !r.placement) add(r.key, t("errorPlacement"));
    }

    if (deadline) {
      const at = new Date(deadline).getTime();
      const now = Date.now();
      if (!(at > now) || at - now > MAX_DEADLINE_MS) formErrors.push(t("errorDeadline"));
    }
    if (message.length > MAX_MESSAGE) formErrors.push(t("errorMessage", MAX_MESSAGE));
    return { errors, formErrors };
  }

  ownEmail() {
    const info = stateService.getState(states.userInfo).value || {};
    return String(oidcService.email() || info.mail || "").toLowerCase() || null;
  }

  organization = () => (this.state.doc ? sessionService.organizationOf(this.state.doc.workspaceExposedId) : null);

  // the request as the backend takes it; the listing only when something
  // is granted
  body(listing) {
    const { doc, recipients, deadline, message, insideOrganization } = this.state;
    let grants = false;
    const body = {
      // the exposed id, the same UUID the signed listing names
      workspaceId: doc.workspaceExposedId,
      recipients: recipients.map((r) => {
        const out = { email: r.email.trim(), permission: r.permission };
        if (r.permission !== "SIGN") return out;
        // a signer match exactly as listed, and no signing tools or kinds
        const match = this.matchOf(r);
        if (match) out.signerMatch = match;
        else {
          if (r.signerIds.length) out.signerIds = r.signerIds;
          if (r.signerKinds.length) out.signerKinds = r.signerKinds;
          grants = grants || r.signerIds.length + r.signerKinds.length > 0;
        }
        if (r.placing && r.placement) out.placement = r.placement;
        // a signed share's own, exactly as it holds it (the same identity
        // again is accepted; any other is refused)
        const locked = this.lockedShareFor(r.email);
        if (locked) {
          if (locked.requiredSignatory) out.requiredSignatory = locked.requiredSignatory;
        } else if (r.signatory)
          out.requiredSignatory = { country: r.country, attributes: { personalNumber: digitsOf(r.personalNumber) } };
        return out;
      }),
    };
    if (deadline) body.deadline = new Date(deadline).getTime();
    if (message.trim()) body.message = message;
    if (this.organization()) body.insideOrganization = !!insideOrganization;
    if (grants && listing) {
      body.signerListing = listing.body;
      body.signerListingSignature = listing.signature;
    }
    return body;
  }

  // ---- sending -------------------------------------------------------------

  // the form, then one placement step per SIGN recipient whose signature
  // place is fixed (Back / Next / Finish / Cancel)
  placingRecipients = () => this.state.recipients.filter((r) => r.permission === "SIGN" && r.placing);

  goToPlacement = () => {
    const { errors, formErrors } = this.check(false);
    if (Object.keys(errors).length || formErrors.length) {
      this.setState({ errors, formErrors, refusal: null });
      return;
    }
    this.loadPlacementContext();
    this.setState({ step: 1, errors: {}, formErrors: [], refusal: null, shared: null, confirmDrop: null });
  };

  submit = async (confirmed = false) => {
    const { errors, formErrors } = this.check();
    if (Object.keys(errors).length || formErrors.length) {
      // the errors are the form's: back to it
      this.setState({ errors, formErrors, refusal: null, step: 0 });
      return;
    }
    this.setState({ sending: true, errors: {}, formErrors: [], refusal: null, confirmDrop: null });
    // a resend without the required signatory clears it: ask first
    if (!confirmed) {
      const found = await Promise.all(
        this.state.recipients
          .filter((r) => !(r.permission === "SIGN" && r.signatory))
          .map((r) => this.lookupActiveShare(r.email))
      );
      if (this.closed) return;
      const drops = found.filter((s) => s && s.requiredSignatory).map((s) => s.email);
      if (drops.length) {
        this.setState({ sending: false, confirmDrop: drops });
        return;
      }
    }
    this.send(this.state.listing, false);
  };

  send(listing, retried) {
    shareService.share(this.state.doc.docExposedId, this.body(listing)).subscribe(
      (res) => {
        if (this.closed) return;
        // the shares changed: the next lookups ask again
        this.lookups = {};
        this.setState({
          sending: false,
          step: 0,
          shared: (res.data && res.data.shares) || [],
          recipients: [emptyRecipient()],
          deadline: "",
          message: "",
          existing: {},
          withdrawn: {},
        });
        if (this.props.onShared) this.props.onShared();
      },
      (e) => {
        if (this.closed) return;
        // an expired listing: fetch it again and resend the same request,
        // once, without a word to the user
        if (!retried && signService.problemCode(e) === "SIGNER_LISTING_EXPIRED") {
          this.loadListing().then((fresh) => {
            if (fresh) this.send(fresh, true);
            else this.setState({ sending: false, refusal: this.t("listingUnavailable") });
          });
          return;
        }
        // e.g. error.shareSignatoryLocked: someone signed through a share
        // since it was looked up - its rows lock and offer the withdrawal
        if (e && e.response && e.response.status === 400) this.refreshLookups();
        this.setState({ sending: false, refusal: signService.problemMessage(e, this.t("shareError")) });
      }
    );
  }

  // ---- rendering -------------------------------------------------------------

  renderGrants(r, index) {
    const t = this.t;
    const signers = this.grantableSigners();
    const kinds = this.grantableKinds();
    const match = this.matchOf(r);
    let choices;
    if (match)
      // read-only: changing it means withdrawing the share and sharing again
      choices = (
        <React.Fragment>
          <p className="share-dialog__criteria">{describeSignerMatch(match)}</p>
          <p className="share-dialog__hint">{translateService.t("signerMatch.readOnly")}</p>
        </React.Fragment>
      );
    else if (this.state.listingLoading || (!this.state.listing && !this.state.listingError))
      choices = <img src="./assets/images/black-loading.svg" className="content-loader" alt="" />;
    else if (this.state.listingError)
      choices = (
        <p className="share-dialog__warning">
          {t("listingUnavailable")}{" "}
          <button type="button" className="share-dialog__link" onClick={this.loadListing}>
            {t("retry")}
          </button>
        </p>
      );
    else if (!signers.length && !kinds.length) choices = <p className="share-dialog__hint">{t("noSigners")}</p>;
    else
      choices = (
        <ul className="share-dialog__choices">
          {kinds.map((k) => {
            const off = r.signatory && k.supportsLimits !== true;
            return (
              <li key={`k-${k.kind}`}>
                <label className={off ? "is-off" : ""} title={off ? t("noLimitsHint") : undefined}>
                  <input
                    type="checkbox"
                    disabled={off && !r.signerKinds.includes(k.kind)}
                    checked={r.signerKinds.includes(k.kind)}
                    onChange={() => this.toggleGrant(index, "signerKinds", k.kind)}
                  />
                  {k.label || k.kind}
                </label>
              </li>
            );
          })}
          {signers.map((s) => {
            const off = r.signatory && s.supportsLimits !== true;
            // its kind, as the Sign menu names it ("Signature protected by a
            // cloud seal"); face verification for a kind without a label here
            const kind =
              translateService.t(`signature.type_${s.type}`) ||
              ((s.signatoryAuthTypes || []).includes("FACE") ? t("faceVerified") : "");
            return (
              <li key={`s-${s.uuid}`}>
                <label className={off ? "is-off" : ""} title={off ? t("noLimitsHint") : undefined}>
                  <input
                    type="checkbox"
                    disabled={off && !r.signerIds.includes(s.uuid)}
                    checked={r.signerIds.includes(s.uuid)}
                    onChange={() => this.toggleGrant(index, "signerIds", s.uuid)}
                  />
                  {s.name}
                  {kind ? ` · ${kind}` : ""}
                </label>
              </li>
            );
          })}
        </ul>
      );

    // signers already granted that the listing no longer offers
    const gone = match ? [] : [
      ...r.signerIds.filter((id) => !signers.some((s) => s.uuid === id)),
      ...r.signerKinds.filter((k) => !kinds.some((x) => x.kind === k)),
    ];

    return (
      <div className="share-dialog__grants">
        <p className="share-dialog__label">{t("signWith")}</p>
        {choices}
        {gone.length > 0 && !this.state.listingLoading && (
          <p className="share-dialog__warning">
            {t("grantsGone", gone.length)}{" "}
            <button
              type="button"
              className="share-dialog__link"
              onClick={() =>
                this.updateRecipient(index, {
                  signerIds: r.signerIds.filter((id) => !gone.includes(id)),
                  signerKinds: r.signerKinds.filter((k) => !gone.includes(k)),
                })
              }
            >
              {t("removeGone")}
            </button>
          </p>
        )}

        {/* a signed share's requirement is shown read-only (renderLock) */}
        {!this.lockedShareFor(r.email) && (
          <label className="share-dialog__check">
            <input
              type="checkbox"
              checked={r.signatory}
              onChange={(e) => this.updateRecipient(index, { signatory: e.target.checked })}
            />
            {t("requireSignatory")}
          </label>
        )}
        {r.signatory && !this.lockedShareFor(r.email) && (
          <div className="share-dialog__signatory" role="group" aria-label={t("requiredSignatory")}>
            <label>
              <span>{t("country")}</span>
              <select value={r.country} onChange={(e) => this.updateRecipient(index, { country: e.target.value })}>
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>
                    {translateService.t(`share.country_${c}`) || c}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>{t("personalNumber")}</span>
              {/* never type="number": it drops a leading zero */}
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9 ]*"
                autoComplete="off"
                maxLength={20}
                value={r.personalNumber}
                placeholder={t("personalNumberHint")}
                onChange={(e) => this.updateRecipient(index, { personalNumber: e.target.value })}
              />
            </label>
            <p className="share-dialog__hint">{t("signatoryHint")}</p>
          </div>
        )}

        <label className="share-dialog__check">
          <input
            type="checkbox"
            checked={r.placing}
            onChange={(e) => {
              const placing = e.target.checked;
              this.updateRecipient(index, { placing, placement: placing ? r.placement : null });
              if (placing) this.loadPlacementContext();
            }}
          />
          {t("fixPlace")}
        </label>
        {r.placing && (
          <p className="share-dialog__hint">{r.placement ? t("placedOn", r.placement.page) : t("placeNext")}</p>
        )}
      </div>
    );
  }

  // the wizard's placement step: where the nth SIGN recipient signs
  renderPlacementStep() {
    const t = this.t;
    const placing = this.placingRecipients();
    const step = Math.min(this.state.step, placing.length);
    const r = placing[step - 1];
    if (!r) return this.renderForm();
    const index = this.state.recipients.findIndex((x) => x.key === r.key);
    const last = step === placing.length;
    const { docFile, box, sending, confirmDrop } = this.state;
    const size = r.placement ? { width: r.placement.width, height: r.placement.height } : box;
    return (
      <React.Fragment>
      {/* the pages fill the step and scroll; the buttons stay below */}
      <div className="share-dialog__scroll share-dialog__scroll--placing">
        <p className="share-dialog__label">{t("placeStepTitle", r.email, step, placing.length)}</p>
        <p className="share-dialog__hint">{r.placement ? t("placedOn", r.placement.page) : t("placeHint")}</p>
        <div className="share-dialog__placement-area">
          {docFile && box ? (
            <PlacementView
              key={r.key}
              file={docFile}
              box={size}
              label={t("signatureBox")}
              value={r.placement ? { page: r.placement.page, x: r.placement.x, y: r.placement.y } : null}
              fixed={false}
              ariaLabel={t("signatureBox")}
              onChange={(v) => this.updateRecipient(index, { placement: { ...v, ...size } })}
            />
          ) : (
            <img src="./assets/images/black-loading.svg" className="content-loader" alt="" />
          )}
        </div>
        {this.renderMessages()}
      </div>
        <div className="buttons share-dialog__footer">
          <LoadingButton
            type="button"
            className="button background-color-one color-white"
            disabled={sending}
            onClick={() => this.setState({ step: step - 1, refusal: null, confirmDrop: null })}
          >
            {t("back")}
          </LoadingButton>
          {last ? (
            <LoadingButton
              type="button"
              className="button background-color-two color-white"
              loading={sending}
              disabled={!r.placement || !!confirmDrop}
              onClick={() => this.submit(false)}
            >
              {t("finish")}
            </LoadingButton>
          ) : (
            <LoadingButton
              type="button"
              className="button background-color-two color-white"
              disabled={!r.placement}
              onClick={() => this.setState({ step: step + 1 })}
            >
              {t("next")}
            </LoadingButton>
          )}
          <LoadingButton
            type="button"
            className="button background-color-three color-white"
            disabled={sending}
            onClick={this.props.onClose}
          >
            {t("cancel")}
          </LoadingButton>
        </div>
      </React.Fragment>
    );
  }

  // what the last check or answer says, on whichever step is shown
  renderMessages() {
    const t = this.t;
    const { formErrors, refusal, shared, confirmDrop } = this.state;
    return (
      <React.Fragment>
        {formErrors.map((e) => (
          <p className="share-dialog__error" key={e}>
            {e}
          </p>
        ))}
        {refusal && (
          <p className="share-dialog__error" role="alert">
            {refusal}
          </p>
        )}
        {shared && (
          <p className="share-dialog__success" role="status">
            {t("sharedWith", shared.map((s) => s.email).join(", "))}
          </p>
        )}
        {confirmDrop && (
          <div className="share-dialog__confirm" role="alert">
            <p>{t("dropSignatory", confirmDrop.join(", "))}</p>
            <div className="buttons">
              <LoadingButton
                type="button"
                className="button background-color-three color-white"
                onClick={() => this.submit(true)}
              >
                {t("dropConfirm")}
              </LoadingButton>
              <LoadingButton
                type="button"
                className="button background-color-one color-white"
                onClick={() => this.setState({ confirmDrop: null })}
              >
                {t("cancel")}
              </LoadingButton>
            </div>
          </div>
        )}
      </React.Fragment>
    );
  }

  // A signed share's required signatory, read-only: its requirement or "none,
  // and none can be added", why, and "withdraw, then share again"
  renderLock(r, locked) {
    const t = this.t;
    const rs = locked.requiredSignatory;
    // READ for a share without one needs no word
    if (!rs && r.permission !== "SIGN") return null;
    const country = rs && (translateService.t(`share.country_${rs.country}`) || rs.country);
    const number = (rs && rs.attributes && rs.attributes.personalNumber) || "";
    const busy = this.state.withdrawing === locked.shareId;
    return (
      <div className="share-dialog__locked" role="group" aria-label={t("requiredSignatory")}>
        <p>
          <span className="share-dialog__label">{t("requiredSignatory")}: </span>
          {rs ? (number ? `${country} · ${number}` : country) : t("signatoryLockedNone")}
        </p>
        <p className="share-dialog__hint">{t("signatoryLocked")}</p>
        {this.state.confirmWithdraw === locked.shareId ? (
          <div className="buttons">
            <span>{translateService.t("shareList.withdrawConfirm", locked.email)}</span>
            <LoadingButton
              type="button"
              className="button background-color-three color-white"
              loading={busy}
              onClick={() => this.withdrawLocked(locked)}
            >
              {translateService.t("shareList.withdrawYes")}
            </LoadingButton>
            <LoadingButton
              type="button"
              className="button background-color-one color-white"
              disabled={busy}
              onClick={() => this.setState({ confirmWithdraw: null })}
            >
              {t("cancel")}
            </LoadingButton>
          </div>
        ) : (
          <button
            type="button"
            className="share-dialog__link"
            disabled={!!this.state.withdrawing}
            onClick={() => this.setState({ confirmWithdraw: locked.shareId })}
          >
            {t("withdrawThenShare")}
          </button>
        )}
      </div>
    );
  }

  renderRecipient = (r, index) => {
    const t = this.t;
    const errors = this.state.errors[r.key] || [];
    const share = this.activeShareFor(r.email);
    const locked = this.lockedShareFor(r.email);
    const withdrawn = !!this.state.withdrawn[String(r.email).trim().toLowerCase()];
    return (
      <fieldset className="share-dialog__recipient" key={r.key}>
        <legend className="sr-only">{t("recipientN", index + 1)}</legend>
        <div className="share-dialog__row">
          <input
            type="email"
            autoComplete="off"
            spellCheck="false"
            maxLength={254}
            placeholder={translateService.t("common.email")}
            aria-label={translateService.t("common.email")}
            aria-invalid={errors.length > 0}
            value={r.email}
            onChange={(e) => this.updateRecipient(index, { email: e.target.value })}
            onBlur={() => this.onEmailBlur(index)}
          />
          <select
            value={r.permission}
            aria-label={t("permission")}
            onChange={(e) => this.updateRecipient(index, { permission: e.target.value })}
          >
            {/* READ would remove a signed share's required signatory */}
            <option value="READ" disabled={!!(locked && locked.requiredSignatory)}>
              {t("permission_READ")}
            </option>
            <option value="SIGN">{t("permission_SIGN")}</option>
          </select>
          {this.state.recipients.length > 1 && (
            <button
              type="button"
              className="share-dialog__remove"
              aria-label={t("removeRecipient")}
              title={t("removeRecipient")}
              onClick={() => this.removeRecipient(index)}
            >
              ×
            </button>
          )}
        </div>
        {share && <p className="share-dialog__hint">{t("alreadyShared")}</p>}
        {withdrawn && !share && <p className="share-dialog__hint">{t("withdrawnNow", r.email.trim())}</p>}
        {locked && this.renderLock(r, locked)}
        {r.permission === "SIGN" && this.renderGrants(r, index)}
        {errors.map((e) => (
          <p className="share-dialog__error" key={e}>
            {e}
          </p>
        ))}
      </fieldset>
    );
  };

  renderForm() {
    const t = this.t;
    const organization = this.organization();
    const { recipients, deadline, message, confirmDrop, sending } = this.state;
    return (
      <React.Fragment>
      {/* the form scrolls; the buttons stay below */}
      <div className="share-dialog__scroll share-dialog__form" ref={(el) => (this.formRef = el)}>
        {recipients.map(this.renderRecipient)}
        <button
          type="button"
          className="share-dialog__link"
          disabled={recipients.length >= MAX_RECIPIENTS}
          onClick={this.addRecipient}
        >
          + {t("addRecipient")}
        </button>

        <label className="share-dialog__field">
          <span>{t("deadline")}</span>
          <input type="datetime-local" value={deadline} onChange={(e) => this.setState({ deadline: e.target.value })} />
          <small>{t("deadlineHint")}</small>
        </label>

        <label className="share-dialog__field">
          <span>{t("message")}</span>
          <textarea
            maxLength={MAX_MESSAGE}
            value={message}
            onChange={(e) => this.setState({ message: e.target.value })}
          />
          <small>
            {message.length}/{MAX_MESSAGE}
          </small>
        </label>

        {organization && (
          <label className="share-dialog__check">
            <input
              type="checkbox"
              checked={this.state.insideOrganization}
              onChange={(e) => this.setState({ insideOrganization: e.target.checked })}
            />
            {t("insideOrganization", organization.name || organization.alias || "")}
            <small>{t(this.state.insideOrganization ? "insideOn" : "insideOff")}</small>
          </label>
        )}

        {this.renderMessages()}
      </div>

        <div className="buttons share-dialog__footer">
          {this.placingRecipients().length ? (
            // a fixed signature place: chosen on the next step(s)
            <LoadingButton
              type="button"
              className="button background-color-two color-white"
              disabled={!!confirmDrop}
              onClick={this.goToPlacement}
            >
              {t("next")}
            </LoadingButton>
          ) : (
            <LoadingButton
              type="button"
              className="button background-color-two color-white"
              loading={sending}
              disabled={!!confirmDrop}
              onClick={() => this.submit(false)}
            >
              {translateService.t("common.share")}
            </LoadingButton>
          )}
          <LoadingButton type="button" className="button background-color-one color-white" onClick={this.props.onClose}>
            {translateService.t("common.close")}
          </LoadingButton>
        </div>
      </React.Fragment>
    );
  }

  render() {
    const t = this.t;
    const { doc, readError } = this.state;
    const name = (doc && (doc.header || doc.docName)) || this.props.docName || "";
    let content;
    const plain = (inner) => <div className="share-dialog__scroll">{inner}</div>;
    if (readError) content = plain(<p className="share-dialog__error">{readError}</p>);
    else if (!doc) content = plain(<img src="./assets/images/black-loading.svg" className="content-loader" alt="" />);
    else if (!(doc.actionRights && doc.actionRights.sharing))
      content = plain(<p className="share-dialog__hint">{t("cannotShare")}</p>);
    else content = this.state.step > 0 ? this.renderPlacementStep() : this.renderForm();

    return (
      <Modal
        isOpen={!!this.props.isOpen}
        fade={false}
        className={`share-dialog ${translateService.key}`}
        toggle={this.props.onClose}
        labelledBy="share-dialog-title"
      >
        <ModalHeader>
          <span id="share-dialog-title" className="share-dialog__title">
            {t("title", name)}
          </span>
          <button
            type="button"
            className="clickable share-dialog__close"
            onClick={this.props.onClose}
            aria-label={translateService.t("common.close")}
          >
            <img src="../../assets/images/close32.svg" alt="" />
          </button>
        </ModalHeader>
        <ModalBody>{content}</ModalBody>
      </Modal>
    );
  }
}
